import env from '@/config/env';

import {
  normalizeNumericEnumValue,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';

import {
  BRANDING_FORM_FIELDS,
  BRANDING_MEDIA_FIELDS,
  MAIN_COLOR_PATTERN,
  WhiteLabelCustomDomainStatus,
  WhiteLabelSource,
} from './constants';

export const unwrapEntity = (response) => {
  if (response == null || response === '') return null;
  if (typeof response !== 'object') return null;
  return response.data ?? response.item ?? response.whiteLabel ?? response;
};

/** Detect GET 204 / empty local config (create mode). */
export const isEmptyLocalResponse = (result) => {
  if (result == null || result === '') return true;
  if (typeof result === 'object' && Object.keys(result).length === 0) return true;
  return false;
};

export const getHttpStatus = (error) =>
  error?.status || error?.response?.status || error?.data?.status || null;

export const toMediaId = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return value.value ?? value.key ?? value.id ?? null;
  }
  return null;
};

export const toBooleanValue = (value, fallback = true) => {
  if (value == null || value === '') return fallback;
  if (typeof value === 'object') {
    const raw = value.value ?? value.key;
    if (raw == null || raw === '') return fallback;
    return raw === true || raw === 'true';
  }
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return value === true || value === 'true' || value === 1 || value === '1';
};

/** Map API local entity → Ant Design form values (labelInValue for media). */
export const mapLocalToFormValues = (local) => {
  if (!local) {
    return {
      isActive: true,
      applyToChildren: false,
      brandName: '',
      mainColor: '#0051FF',
      supportEmail: '',
    };
  }

  const values = {
    isActive: toBooleanValue(local.isActive, true),
    applyToChildren: !!local.applyToChildren,
    brandName: local.brandName ?? '',
    mainColor: local.mainColor ?? '#0051FF',
    supportEmail: local.supportEmail ?? '',
  };

  BRANDING_MEDIA_FIELDS.forEach((field) => {
    const mediaId = local[field];
    const nestedKey = field.replace(/MediaId$/, 'Media');
    const nested = local[nestedKey];
    const label =
      nested?.name ||
      nested?.fileName ||
      local[`${field.replace(/MediaId$/, 'MediaName')}`] ||
      local[`${field.replace(/Id$/, 'Name')}`];
    values[field] = mediaId
      ? { value: mediaId, label: label || mediaId }
      : undefined;
  });

  return values;
};

/** Build PUT body from form values. Omit `version` when creating. */
export const buildBrandingPayload = (values, { version, isCreate } = {}) => {
  const brandName = (values.brandName ?? '').trim();
  const mainColor = String(values.mainColor ?? '')
    .trim()
    .toUpperCase();
  const supportEmail = (values.supportEmail ?? '').trim();

  const payload = {
    isActive: toBooleanValue(values.isActive, true),
    applyToChildren: !!values.applyToChildren,
    brandName: brandName || null,
    mainColor: MAIN_COLOR_PATTERN.test(mainColor) ? mainColor : null,
    supportEmail: supportEmail || null,
  };

  BRANDING_MEDIA_FIELDS.forEach((field) => {
    payload[field] = toMediaId(values[field]);
  });

  if (!isCreate && version != null) {
    payload.version = version;
  }

  return payload;
};

export const resolveMediaPreviewUrl = (mediaId, legacyUrl) => {
  if (legacyUrl) return legacyUrl;
  if (!mediaId) return null;
  const base = (env.ASSETS_URL || '').replace(/\/$/, '');
  return base ? `${base}/media/${mediaId}/view` : null;
};

export const pickLegacyUrl = (entity, keys) => {
  if (!entity) return null;
  for (let i = 0; i < keys.length; i += 1) {
    const value = entity[keys[i]];
    if (value) return value;
  }
  return null;
};

export const normalizeWhiteLabelSource = (raw, fallback = WhiteLabelSource.Default) =>
  normalizeNumericEnumValue(WhiteLabelSource, raw, fallback);

export const normalizeWhiteLabelCustomDomainStatus = (
  raw,
  fallback = WhiteLabelCustomDomainStatus.NotConfigured,
) => normalizeNumericEnumValue(WhiteLabelCustomDomainStatus, raw, fallback);

export const resolveEffectiveSource = (effective, local) => {
  if (!effective) return WhiteLabelSource.Default;

  const raw =
    effective.effectiveSource ??
    effective.source ??
    effective.brandingSource ??
    effective.resolutionSource;

  const parsed = parseOptionalNumericEnumFromApi(WhiteLabelSource, raw);
  if (parsed !== undefined) return parsed;

  if (local?.id && effective.companyId && local.companyId === effective.companyId) {
    return WhiteLabelSource.Own;
  }
  if (local?.isActive && local?.brandName) {
    return WhiteLabelSource.Own;
  }
  if (effective.isDefault || effective.usesDefault) return WhiteLabelSource.Default;
  if (effective.isInherited || effective.inheritedFromCompanyId) {
    return WhiteLabelSource.Inherited;
  }
  if (effective.brandName || effective.mainColor || effective.logoMediaId) {
    return local ? WhiteLabelSource.Own : WhiteLabelSource.Inherited;
  }
  return WhiteLabelSource.Default;
};

export const normalizeHostname = (value) => {
  if (!value || typeof value !== 'string') return '';
  let host = value.trim().toLowerCase();
  host = host.replace(/^https?:\/\//, '');
  host = host.split('/')[0];
  host = host.split('?')[0];
  host = host.split('#')[0];
  host = host.replace(/:\d+$/, '');
  return host;
};

export const isReservedAutomyDomain = (hostname) => {
  const host = normalizeHostname(hostname);
  return host === 'automy.global' || host.endsWith('.automy.global');
};

export const isValidCustomHostname = (value) => {
  const host = normalizeHostname(value);
  if (!host || host.includes(' ') || host.includes('/') || host.includes(':')) return false;
  if (isReservedAutomyDomain(host)) return false;
  // labels of 1–63 chars, no leading/trailing hyphen, at least one dot
  if (!/^[a-z0-9.-]+$/i.test(host)) return false;
  if (host.startsWith('.') || host.endsWith('.') || host.includes('..')) return false;
  const labels = host.split('.');
  if (labels.length < 2) return false;
  return labels.every((label) => /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i.test(label));
};

export const getDomainStatusLabelId = (status) => {
  const normalized = normalizeWhiteLabelCustomDomainStatus(status);
  switch (normalized) {
    case WhiteLabelCustomDomainStatus.PendingVerification:
      return 'whiteLabel.domain.status.pending';
    case WhiteLabelCustomDomainStatus.Verified:
      return 'whiteLabel.domain.status.verified';
    case WhiteLabelCustomDomainStatus.Active:
      return 'whiteLabel.domain.status.active';
    case WhiteLabelCustomDomainStatus.Disabled:
      return 'whiteLabel.domain.status.disabled';
    default:
      return 'whiteLabel.domain.status.notConfigured';
  }
};

/** Maps domain status → `@/components/Badge` status (same pattern as AI Billing). */
export const getDomainStatusBadgeStatus = (status) => {
  const normalized = normalizeWhiteLabelCustomDomainStatus(status);
  switch (normalized) {
    case WhiteLabelCustomDomainStatus.PendingVerification:
      return 'pending';
    case WhiteLabelCustomDomainStatus.Verified:
      return 'info';
    case WhiteLabelCustomDomainStatus.Active:
      return 'success';
    case WhiteLabelCustomDomainStatus.Disabled:
      return 'disabled';
    default:
      return 'default';
  }
};

export const getEffectiveSourceLabelId = (source) => {
  const normalized = normalizeWhiteLabelSource(source);
  switch (normalized) {
    case WhiteLabelSource.Own:
      return 'whiteLabel.effective.source.own';
    case WhiteLabelSource.Inherited:
      return 'whiteLabel.effective.source.inherited';
    default:
      return 'whiteLabel.effective.source.default';
  }
};

export const getEffectiveSourceBadgeStatus = (source) => {
  const normalized = normalizeWhiteLabelSource(source);
  switch (normalized) {
    case WhiteLabelSource.Own:
      return 'info';
    case WhiteLabelSource.Inherited:
      return 'pending';
    default:
      return 'default';
  }
};

export const mergeDomainResponse = (local, response) => {
  if (!response) return local;
  return {
    ...(local || {}),
    ...response,
    version: response.version ?? local?.version,
  };
};

export const snapshotBrandingValues = (values) => {
  const snapshot = {};
  BRANDING_FORM_FIELDS.forEach((key) => {
    if (BRANDING_MEDIA_FIELDS.includes(key)) {
      snapshot[key] = toMediaId(values?.[key]);
    } else if (key === 'brandName' || key === 'supportEmail') {
      snapshot[key] = (values?.[key] ?? '').trim();
    } else if (key === 'mainColor') {
      snapshot[key] = String(values?.[key] ?? '')
        .trim()
        .toUpperCase();
    } else if (key === 'applyToChildren') {
      snapshot[key] = !!values?.[key];
    } else if (key === 'isActive') {
      snapshot[key] = toBooleanValue(values?.[key], true);
    } else {
      snapshot[key] = values?.[key];
    }
  });
  return snapshot;
};

export const isBrandingDirty = (baseline, current) => {
  if (!baseline) return false;
  const left = snapshotBrandingValues(baseline);
  const right = snapshotBrandingValues(current);
  return BRANDING_FORM_FIELDS.some((key) => left[key] !== right[key]);
};
